// ============================================================================
// MarketDisputeHistory — история спора (сообщения обсуждения заявки).
// Служебные сообщения (открытие/решение) — выделенные блоки; остальные —
// комментарии сторон и оператора. Граница приватности — момент открытия спора
// (history уже обрезана в loadMarketDispute).
// ============================================================================

import { AlertTriangle, CheckCircle2, ShieldAlert } from 'lucide-react';
import { formatValue } from '@/lib/format';
import {
  DISPUTE_DECISION_LABELS,
  disputeReasonLabel,
  type DisputeHistoryItem,
} from '@/lib/marketDispute';

function when(value: string): string {
  return formatValue(value, 'datetime:dd.MM.yyyy HH:mm', 'timestamp');
}

export function MarketDisputeHistory({
  items,
  historyError,
}: {
  items: DisputeHistoryItem[];
  historyError: string;
}) {
  return (
    <>
      {historyError && (
        <div className="mt-2 flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] leading-5 text-amber-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{historyError}</span>
        </div>
      )}
      <div className="mt-3 space-y-2">
        {items.length === 0 && !historyError ? (
          <div className="rounded-xl border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">
            Сообщений пока нет.
          </div>
        ) : (
          items.map((item) => {
            if (item.marker?.kind === 'opened') {
              return (
                <div key={item.id} className="rounded-xl border border-red-500/30 bg-red-500/5 px-3 py-2">
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold text-red-700">
                    <ShieldAlert className="h-3.5 w-3.5" />
                    Спор открыт · {item.marker.role === 'master' ? 'мастер' : 'заказчик'}
                  </div>
                  <div className="mt-1 text-[11px] text-muted-foreground">
                    {item.senderName} · {when(item.createdAt)}
                  </div>
                  <div className="mt-1 text-xs font-medium">
                    {disputeReasonLabel(item.marker.reason || '')}
                  </div>
                  {item.marker.comment && (
                    <p className="mt-1 whitespace-pre-wrap break-words text-xs leading-5 text-muted-foreground">
                      {item.marker.comment}
                    </p>
                  )}
                </div>
              );
            }
            if (item.marker?.kind === 'decision') {
              return (
                <div key={item.id} className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-3 py-2">
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold text-emerald-800">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Решение: {DISPUTE_DECISION_LABELS[item.marker.decision || 'reject']}
                  </div>
                  <div className="mt-1 text-[11px] text-muted-foreground">
                    {item.senderName} · {when(item.createdAt)}
                  </div>
                  {item.marker.comment && (
                    <p className="mt-1 whitespace-pre-wrap break-words text-xs leading-5 text-muted-foreground">
                      {item.marker.comment}
                    </p>
                  )}
                </div>
              );
            }
            return (
              <div key={item.id} className="rounded-xl border bg-background/60 px-3 py-2">
                <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
                  <span className="truncate font-medium text-foreground">{item.senderName || 'Участник'}</span>
                  <span className="shrink-0">{when(item.createdAt)}</span>
                </div>
                <p className={`mt-1 whitespace-pre-wrap break-words text-xs leading-5 ${item.isDeleted ? 'italic text-muted-foreground' : ''}`}>
                  {item.isDeleted ? 'Сообщение удалено' : item.text}
                </p>
              </div>
            );
          })
        )}
      </div>
    </>
  );
}
